// src/pages/admin/ReportsPane.js
import { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function ReportsPane() {
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    regular: 0,
    nominal: 0,
  });
  const [portalSettings, setPortalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        // load members
        const snap = await getDocs(
          query(collection(db, "members"), orderBy("created_at", "desc"))
        );
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));

        const total = list.length;
        const approved = list.filter((m) => m.status === "approved").length;
        const pending = list.filter((m) => m.status === "pending" || !m.status)
          .length;
        const rejected = list.filter((m) => m.status === "rejected").length;
        const regular = list.filter((m) => m.membership_type === "REGULAR")
          .length;
        const nominal = list.filter(
          (m) => (m.membership_type || "NOMINAL") === "NOMINAL"
        ).length;

        setStats({ total, approved, pending, rejected, regular, nominal });

        // load portal settings
        const ref = doc(db, "portal_settings", "global");
        const psnap = await getDoc(ref);
        if (psnap.exists()) {
          setPortalSettings(psnap.data());
        } else {
          setPortalSettings(null);
        }
      } catch (err) {
        console.error("Reports load error:", err);
        setError(err.message || "Failed to load reports data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading report…</div>;
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg md:text-xl font-bold text-slate-900">
          Summary &amp; High-level Report
        </h2>
        <p className="text-xs md:text-sm text-slate-500 mt-0.5">
          Quick overview of member statistics and portal configuration for the
          Society.
        </p>
      </div>

      {error && (
        <div className="mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
          ⚠ Firestore error while loading reports:{" "}
          <span className="font-mono">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Members" value={stats.total} tone="emerald" />
        <StatCard label="Approved" value={stats.approved} tone="sky" />
        <StatCard label="Pending" value={stats.pending} tone="amber" />
        <StatCard label="Rejected" value={stats.rejected} tone="rose" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Regular Members"
          value={stats.regular}
          tone="indigo"
          small
        />
        <StatCard
          label="Nominal Members"
          value={stats.nominal}
          tone="lime"
          small
        />
        <PortalSnapshot portalSettings={portalSettings} />
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "emerald", small = false }) {
  const colorMap = {
    emerald: "from-emerald-500 to-emerald-600",
    sky: "from-sky-500 to-sky-600",
    amber: "from-amber-500 to-amber-600",
    rose: "from-rose-500 to-rose-600",
    indigo: "from-indigo-500 to-indigo-600",
    lime: "from-lime-500 to-lime-600",
  }[tone];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div
        className={cx(
          "px-4 py-2 text-[11px] font-semibold text-white bg-gradient-to-r",
          colorMap
        )}
      >
        {label}
      </div>
      <div className="px-4 py-3">
        <div
          className={cx(
            "font-extrabold text-slate-900",
            small ? "text-xl" : "text-2xl"
          )}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function PortalSnapshot({ portalSettings }) {
  if (!portalSettings) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm text-xs md:text-sm">
        <div className="font-semibold text-slate-900 mb-2">
          Portal Status Snapshot
        </div>
        <div className="text-xs text-slate-500">
          No <code>portal_settings/global</code> document found yet.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm text-xs md:text-sm">
      <div className="font-semibold text-slate-900 mb-2">
        Portal Status Snapshot
      </div>
      <ul className="space-y-1 text-slate-600">
        <li>
          {portalSettings.new_regular_open ? "✅" : "⛔"} New Regular Registration
        </li>
        <li>
          {portalSettings.new_nominal_open ? "✅" : "⛔"} New Nominal Registration
        </li>
        <li>
          {portalSettings.member_login_open ? "✅" : "⛔"} Member Login
        </li>
        <li>
          {portalSettings.dealer_portal_open ? "✅" : "⛔"} Dealer Portal
        </li>
      </ul>
      <div className="mt-2 text-[11px] text-slate-400">
        Last updated:{" "}
        {portalSettings.updated_at
          ? new Date(portalSettings.updated_at).toLocaleString()
          : "—"}
      </div>
    </div>
  );
}

export default ReportsPane;
